import { useState, useMemo, useContext, useCallback } from 'react'
import { TelemetryDataContext } from '../context/TelemetryDataContext'
import { SessionDataContext } from '../context/SessionDataContext'
import { getGuiSessionConfig } from '../../workspace/config/guiSessionConfig'
import ReplayWorkspace from '../../workspaceReplay2/ReplayWorkspace'
import ReplayControls from '../../workspaceReplay2/components/ReplayControls'
import { usePlayback } from '../../workspaceReplay2/hooks/usePlayback'
import { autoSegmentEvents } from '../../workspaceReplay2/utils/autoSegmentation'

// ─── Helpers ────────────────────────────────────────────────────────────────────

function extractErrorType(errorPayload) {
  // GUI/web-dev sessions' TERMINAL_ERROR carries a generic count message ("2 syntax issues
  // found") that none of the regex/substring checks below can parse - they're written for
  // Python tracebacks - but a specific `type` field (see TerminalContext.js's runTestsWebDev),
  // so check it first rather than falling through to "Other" for every GUI-session error.
  if (errorPayload?.type === 'WebDevCompilationError') return 'Syntax/Compilation Error'

  const msg = errorPayload?.message || errorPayload?.error || errorPayload?.text || ''
  const match = msg.match(/^(\w*Error|\w*Exception|IndentationError|TabError|KeyboardInterrupt)/)
  if (match) return match[1]
  if (msg.includes('SyntaxError')) return 'SyntaxError'
  if (msg.includes('IndentationError')) return 'IndentationError'
  if (msg.includes('NameError')) return 'NameError'
  if (msg.includes('TypeError')) return 'TypeError'
  if (msg.includes('IndexError')) return 'IndexError'
  if (msg.includes('ValueError')) return 'ValueError'
  if (msg.includes('AttributeError')) return 'AttributeError'
  if (msg.includes('KeyError')) return 'KeyError'
  if (msg.includes('ZeroDivisionError')) return 'ZeroDivisionError'
  if (msg.includes('ImportError')) return 'ImportError'
  if (msg.includes('ModuleNotFoundError')) return 'ModuleNotFoundError'
  if (msg.includes('FileNotFoundError')) return 'FileNotFoundError'
  if (msg.includes('RuntimeError')) return 'RuntimeError'
  if (msg.includes('StopIteration')) return 'StopIteration'
  if (msg.includes('RecursionError')) return 'RecursionError'
  if (msg.includes('Traceback')) return 'Unknown Runtime Error'
  return 'Other'
}

function formatTime(ms) {
  const totalSeconds = Math.floor(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s < 10 ? '0' : ''}${s}`
}

const ERROR_TYPE_COLORS = {
  SyntaxError: '#EF4444',
  IndentationError: '#F97316',
  NameError: '#F59E0B',
  TypeError: '#8B5CF6',
  IndexError: '#3B82F6',
  ValueError: '#06B6D4',
  AttributeError: '#EC4899',
  KeyError: '#10B981',
  ZeroDivisionError: '#6366F1',
  ImportError: '#14B8A6',
  Other: '#9CA3AF',
  'Unknown Runtime Error': '#6B7280',
  'Syntax/Compilation Error': '#EF4444',
}

// ─── Error Group Panel ──────────────────────────────────────────────────────────

function ErrorGroupPanel({ groups, allStudents, selectedGroup, selectedStudentId, onSelectGroup, onSelectStudent }) {
  const totalStudents = new Set(Object.values(groups).flatMap(g => g.map(s => s.id))).size
  const totalErrors = Object.values(groups).reduce((sum, g) => sum + g.reduce((s, st) => s + st.errorCount, 0), 0)
  const [allExpanded, setAllExpanded] = useState(true)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Summary */}
      <div style={{
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        flexShrink: 0,
      }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: '#111' }}>Error Groups</div>
        <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
          {totalStudents} students · {totalErrors} total errors
        </div>
      </div>

      {/* All students - error groups only surface students who logged a TERMINAL_ERROR,
          which left every other student (the common case for anyone who didn't hit an
          error) with no way to be selected for replay at all. This lists everyone so
          replay isn't gated on having errored. */}
      <div style={{ borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <button
          onClick={() => setAllExpanded(!allExpanded)}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
            padding: '10px 20px', border: 'none', background: 'white',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#111' }}>All Students</div>
            <div style={{ fontSize: '10px', color: '#888', marginTop: '1px' }}>
              {allStudents.length} student{allStudents.length !== 1 ? 's' : ''}
            </div>
          </div>
          <span style={{
            fontSize: '11px', color: '#888',
            transform: allExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s',
          }}>
            ▶
          </span>
        </button>
        {allExpanded && (
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {allStudents.map((student, idx) => {
              const isSelected = selectedStudentId === student.id
              return (
                <button
                  key={student.id}
                  onClick={() => onSelectStudent(student)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 12px 8px 20px', border: 'none',
                    borderTop: idx > 0 ? '1px solid #f0f0f2' : 'none',
                    background: isSelected ? '#e0e7ff' : 'white',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <div style={{
                    width: '24px', height: '24px', borderRadius: '50%',
                    backgroundColor: '#f0f0f2', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                    </svg>
                  </div>
                  <div style={{
                    fontSize: '11px', fontWeight: 500, color: '#111', fontFamily: 'monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {student.name}
                  </div>
                </button>
              )
            })}
            {allStudents.length === 0 && (
              <div style={{ padding: '16px 20px', textAlign: 'center', color: '#888', fontSize: '12px' }}>
                No students yet
              </div>
            )}
          </div>
        )}
      </div>

      {/* Groups list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
        {Object.entries(groups)
          .sort(([, a], [, b]) => b.reduce((s, st) => s + st.errorCount, 0) - a.reduce((s, st) => s + st.errorCount, 0))
          .map(([errorType, students]) => {
            const isExpanded = selectedGroup === errorType
            const groupErrors = students.reduce((s, st) => s + st.errorCount, 0)
            const color = ERROR_TYPE_COLORS[errorType] || '#9CA3AF'

            return (
              <div key={errorType} style={{ marginBottom: '4px' }}>
                {/* Group header */}
                <button
                  onClick={() => onSelectGroup(isExpanded ? null : errorType)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    border: isExpanded ? `1px solid ${color}40` : '1px solid #e5e7eb',
                    borderRadius: isExpanded ? '8px 8px 0 0' : '8px',
                    background: isExpanded ? `${color}08` : 'white',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{
                    width: '10px', height: '10px', borderRadius: '3px',
                    backgroundColor: color, flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '12px', fontWeight: 600, color: '#111',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {errorType}
                    </div>
                    <div style={{ fontSize: '10px', color: '#888', marginTop: '1px' }}>
                      {students.length} student{students.length !== 1 ? 's' : ''} · {groupErrors} error{groupErrors !== 1 ? 's' : ''}
                    </div>
                  </div>
                  <span style={{
                    fontSize: '11px', color: '#888',
                    transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.15s',
                  }}>
                    ▶
                  </span>
                </button>

                {/* Expanded student list */}
                {isExpanded && (
                  <div style={{
                    border: `1px solid ${color}40`,
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                    overflow: 'hidden',
                  }}>
                    {students
                      .sort((a, b) => b.errorCount - a.errorCount)
                      .map((student, idx) => {
                        const isSelected = selectedStudentId === student.id
                        return (
                          <button
                            key={student.id}
                            onClick={() => onSelectStudent(student)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '8px 12px 8px 32px',
                              border: 'none',
                              borderTop: idx > 0 ? '1px solid #f0f0f2' : 'none',
                              background: isSelected ? `${color}15` : 'white',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'background 0.1s',
                              position: 'relative',
                            }}
                          >
                            {isSelected && (
                              <div style={{
                                width: '3px', height: '20px',
                                backgroundColor: color, borderRadius: '2px',
                                position: 'absolute', left: '12px',
                              }} />
                            )}
                            <div style={{
                              width: '24px', height: '24px', borderRadius: '50%',
                              backgroundColor: '#f0f0f2', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                              </svg>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{
                                fontSize: '11px', fontWeight: 500, color: '#111',
                                fontFamily: 'monospace',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              }}>
                                {student.name}
                              </div>
                            </div>
                            <span style={{
                              fontSize: '10px', fontWeight: 600, color: color,
                              padding: '2px 6px', borderRadius: '4px',
                              backgroundColor: `${color}15`,
                            }}>
                              {student.errorCount}×
                            </span>
                          </button>
                        )
                      })}
                  </div>
                )}
              </div>
            )
          })}

        {Object.keys(groups).length === 0 && (
          <div style={{
            padding: '32px 16px', textAlign: 'center',
            color: '#888', fontSize: '12px',
          }}>
            No errors recorded yet
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Main Presenter Tab ─────────────────────────────────────────────────────────

export default function PresenterTab({ students }) {
  const { eventsByUser } = useContext(TelemetryDataContext)
  const { session, sessionData, isGui } = useContext(SessionDataContext)
  // SessionDataContext already fetches the full session (test_code, type, etc.) for the
  // task-description sync above - reusing it here rather than a second fetch. Null for any
  // non-GUI session, in which case ReplayWorkspace falls back to its original generic
  // template exactly as before this change.
  const guiConfig = useMemo(() => (session ? getGuiSessionConfig(session) : null), [session])

  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedStudent, setSelectedStudent] = useState(null)

  // Build error groups from telemetry
  const errorGroups = useMemo(() => {
    const groups = {}

    students.forEach(student => {
      const uid = String(student.id || student.user_id)
      const events = eventsByUser[uid] || []
      const errors = events.filter(e => e.type === 'TERMINAL_ERROR')
      if (errors.length === 0) return

      const typeCounts = {}
      errors.forEach(e => {
        const errorType = extractErrorType(e.payload)
        typeCounts[errorType] = (typeCounts[errorType] || 0) + 1
      })

      const name = student.display_name || student.username
        || `${student.first_name || ''} ${student.last_name || ''}`.trim()
        || 'Unknown'

      Object.entries(typeCounts).forEach(([errorType, count]) => {
        if (!groups[errorType]) groups[errorType] = []
        groups[errorType].push({ id: student.id, name, errorCount: count })
      })
    })

    return groups
  }, [students, eventsByUser])

  // Full roster, independent of error groups - see ErrorGroupPanel's "All Students" section.
  const allStudents = useMemo(() => {
    return students
      .map(student => ({
        id: student.id,
        name: student.display_name || student.username
          || `${student.first_name || ''} ${student.last_name || ''}`.trim()
          || 'Unknown',
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [students])

  // Get replay data for selected student
  const replayData = useMemo(() => {
    if (!selectedStudent) return null

    const uid = String(selectedStudent.id)
    const events = eventsByUser[uid] || []
    if (events.length === 0) return null

    const filtered = events
      .filter(e => e.timestamp)
      .sort((a, b) => a.timestamp - b.timestamp)

    if (filtered.length === 0) return null

    const startTime = filtered[0].timestamp
    const endTime = filtered[filtered.length - 1].timestamp
    const duration = endTime - startTime

    const segments = autoSegmentEvents(filtered, startTime)

    return { events: filtered, startTime, duration, segments }
  }, [selectedStudent, eventsByUser])

  const playback = usePlayback(replayData?.duration || 0)

  // Navigation within a group
  const groupStudents = selectedGroup ? errorGroups[selectedGroup] || [] : []
  const currentIdx = selectedStudent
    ? groupStudents.findIndex(s => s.id === selectedStudent.id)
    : -1

  const handlePrev = useCallback(() => {
    if (currentIdx > 0) {
      setSelectedStudent(groupStudents[currentIdx - 1])
      playback.reset()
    }
  }, [currentIdx, groupStudents, playback])

  const handleNext = useCallback(() => {
    if (currentIdx < groupStudents.length - 1) {
      setSelectedStudent(groupStudents[currentIdx + 1])
      playback.reset()
    }
  }, [currentIdx, groupStudents, playback])

  const handleSelectStudent = useCallback((student) => {
    setSelectedStudent(student)
    playback.reset()
  }, [playback])

  return (
    <div style={{
      display: 'flex', height: '100%', overflow: 'hidden',
      backgroundColor: '#f3f4f6', borderRadius: '12px',
    }}>
      {/* ── Left: Replay Area ── */}
      <div style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        overflow: 'hidden', backgroundColor: '#1f2937',
        borderRadius: '12px 0 0 12px',
      }}>
        {selectedStudent && replayData ? (
          <>
            {/* Student header bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px',
              backgroundColor: '#111827',
              borderBottom: '1px solid #374151',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  backgroundColor: '#374151', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#F9FAFB', fontFamily: 'monospace' }}>
                    {selectedStudent.name}
                  </div>
                  <div style={{ fontSize: '10px', color: '#9CA3AF' }}>
                    {selectedGroup && (
                      <span style={{ color: ERROR_TYPE_COLORS[selectedGroup] || '#9CA3AF' }}>
                        {selectedGroup} · {selectedStudent.errorCount}×
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Prev / Next */}
              {groupStudents.length > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    onClick={handlePrev}
                    disabled={currentIdx <= 0}
                    style={{
                      padding: '4px 10px', borderRadius: '6px',
                      border: '1px solid #374151',
                      background: currentIdx <= 0 ? '#1f2937' : '#374151',
                      color: currentIdx <= 0 ? '#4B5563' : '#D1D5DB',
                      fontSize: '12px', fontWeight: 600,
                      cursor: currentIdx <= 0 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    ← Prev
                  </button>
                  <span style={{ fontSize: '11px', color: '#6B7280', minWidth: '40px', textAlign: 'center' }}>
                    {currentIdx + 1}/{groupStudents.length}
                  </span>
                  <button
                    onClick={handleNext}
                    disabled={currentIdx >= groupStudents.length - 1}
                    style={{
                      padding: '4px 10px', borderRadius: '6px',
                      border: '1px solid #374151',
                      background: currentIdx >= groupStudents.length - 1 ? '#1f2937' : '#374151',
                      color: currentIdx >= groupStudents.length - 1 ? '#4B5563' : '#D1D5DB',
                      fontSize: '12px', fontWeight: 600,
                      cursor: currentIdx >= groupStudents.length - 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>

            {/* Replay workspace */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              <div style={{
                width: '133.33%', height: '133.33%',
                transform: 'scale(0.75)', transformOrigin: 'top left',
              }}>
                <ReplayWorkspace
                  playback={playback}
                  events={replayData.events}
                  startTime={replayData.startTime}
                  task={sessionData?.taskDescription || ''}
                  starterCode={guiConfig?.starterCode || sessionData?.starterCode || ''}
                  isGuiSession={isGui}
                  guiConfig={guiConfig}
                />
              </div>
            </div>

            {/* Replay controls */}
            <ReplayControls
              currentTime={playback.currentTime}
              duration={playback.duration}
              isPlaying={playback.isPlaying}
              playbackSpeed={playback.playbackSpeed}
              onTogglePlay={playback.togglePlay}
              onSeek={playback.seek}
              onReset={playback.reset}
              onCycleSpeed={playback.cycleSpeed}
              taggedBlocks={replayData.segments.filter(s => !s.isSystemEvent)}
              showLabeling={false}
              activeRecording={null}
              isAutoMode={true}
              autoSegments={replayData.segments}
              onAssignBehavior={() => {}}
              formatTime={formatTime}
              viewOnly={true}
              events={replayData.events}
              startTime={replayData.startTime}
            />
          </>
        ) : (
          /* Empty state */
          <div style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: '12px',
          }}>
            <div style={{
              width: '64px', height: '64px', borderRadius: '50%',
              backgroundColor: '#374151', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
            </div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#9CA3AF' }}>
              Select a student to replay
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280', maxWidth: '280px', textAlign: 'center', lineHeight: 1.5 }}>
              Pick a student from All Students, or an error group, on the right
            </div>
          </div>
        )}
      </div>

      {/* ── Right: Error Groups Panel ── */}
      <div style={{
        width: '280px', flexShrink: 0,
        backgroundColor: 'white',
        borderLeft: '1px solid #e5e7eb',
        borderRadius: '0 12px 12px 0',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <ErrorGroupPanel
          groups={errorGroups}
          allStudents={allStudents}
          selectedGroup={selectedGroup}
          selectedStudentId={selectedStudent?.id}
          onSelectGroup={setSelectedGroup}
          onSelectStudent={handleSelectStudent}
        />
      </div>
    </div>
  )
}